/**
 * The player's inventory: 36 main slots (0..8 are the hotbar), 4 armor slots
 * and the offhand — vanilla's layout. Pure data + rules; the screens and HUD
 * render it, the world code adds to it.
 */

import {
  cloneStack, clearStack, deserializeExtras, emptyStack, enchKey, isEmpty, serializeExtras, setStack, type Ench, type Stack,
} from './types';
import { maxDamageOf, maxStackOf } from './itemdb';

export const HOTBAR = 9;
export const MAIN_SLOTS = 36;

export class Inventory {
  /** 0..8 hotbar, 9..35 backpack (row-major, top row first). */
  readonly slots: Stack[] = Array.from({ length: MAIN_SLOTS }, emptyStack);
  /** 0 helmet, 1 chestplate, 2 leggings, 3 boots. */
  readonly armor: Stack[] = Array.from({ length: 4 }, emptyStack);
  readonly offhand: Stack = emptyStack();
  selected = 0;
  /** Fired after any mutation made through this class; UI code that edits
   *  stacks directly must call `changed()` itself. */
  onChange: (() => void) | null = null;

  changed(): void { this.onChange?.(); }

  /** The stack in the selected hotbar slot (may be empty). */
  get held(): Stack { return this.slots[this.selected]; }

  setSelected(i: number): void {
    this.selected = ((i % HOTBAR) + HOTBAR) % HOTBAR;
    this.changed();
  }

  /** Room for at least one more of `id`? */
  canAdd(id: number, count = 1, dmg = 0, ench?: Ench[]): boolean {
    return this.simulateAdd(id, count, dmg, ench) === 0;
  }

  private simulateAdd(id: number, count: number, dmg: number, ench?: Ench[]): number {
    const max = maxStackOf(id);
    const ek = enchKey({ id, count, dmg, ench });
    let left = count;
    for (const s of this.slots) {
      if (left <= 0) break;
      if (isEmpty(s)) left -= max;
      else if (s.id === id && s.dmg === dmg && enchKey(s) === ek && s.count < max) left -= max - s.count;
    }
    return Math.max(0, left);
  }

  /**
   * Add items the way vanilla picks them up: top up matching stacks first
   * (hotbar then backpack), then fill empty slots (hotbar first).
   * Returns how many did NOT fit.
   */
  add(id: number, count = 1, dmg = 0, ench?: Ench[]): number {
    if (id <= 0 || count <= 0) return 0;
    const max = maxStackOf(id);
    const ek = enchKey({ id, count, dmg, ench });
    let left = count;
    if (max > 1) {
      for (const s of this.slots) {
        if (left <= 0) break;
        if (!isEmpty(s) && s.id === id && s.dmg === dmg && enchKey(s) === ek && s.count < max) {
          const n = Math.min(max - s.count, left);
          s.count += n;
          left -= n;
        }
      }
    }
    for (const s of this.slots) {
      if (left <= 0) break;
      if (isEmpty(s)) {
        const n = Math.min(max, left);
        setStack(s, { id, count: n, dmg, ench });
        left -= n;
      }
    }
    if (left !== count) this.changed();
    return left;
  }

  countOf(id: number): number {
    let n = 0;
    for (const s of this.slots) if (s.id === id) n += s.count;
    return n;
  }

  /** Remove `count` of `id` from anywhere in the main slots; false (and no change) if not enough. */
  remove(id: number, count = 1): boolean {
    if (this.countOf(id) < count) return false;
    let left = count;
    // vanilla drains the selected/hotbar slots first
    for (const s of this.slots) {
      if (left <= 0) break;
      if (s.id !== id) continue;
      const n = Math.min(s.count, left);
      s.count -= n;
      left -= n;
      if (s.count <= 0) clearStack(s);
    }
    this.changed();
    return true;
  }

  /** Use up `n` items of the held stack (placing a block, eating). */
  consumeHeld(n = 1): void {
    const s = this.held;
    if (isEmpty(s)) return;
    s.count -= n;
    if (s.count <= 0) clearStack(s);
    this.changed();
  }

  /**
   * Wear down a damageable stack by `amount`. Returns true when it broke
   * (the slot is cleared). Non-damageable items are untouched.
   */
  damage(s: Stack, amount = 1): boolean {
    const max = maxDamageOf(s.id);
    if (max <= 0 || isEmpty(s)) return false;
    s.dmg += amount;
    if (s.dmg >= max) {
      clearStack(s);
      this.changed();
      return true;
    }
    this.changed();
    return false;
  }

  /** Wear the held tool. Returns true if it broke. */
  damageHeld(amount = 1): boolean { return this.damage(this.held, amount); }

  clear(): void {
    for (const s of this.slots) clearStack(s);
    for (const s of this.armor) clearStack(s);
    clearStack(this.offhand);
    this.changed();
  }

  /** Every non-empty stack (main, armor, offhand) — used to scatter on death. */
  drainAll(): Stack[] {
    const out: Stack[] = [];
    for (const s of [...this.slots, ...this.armor, this.offhand]) {
      if (!isEmpty(s)) out.push(cloneStack(s));
      clearStack(s);
    }
    this.changed();
    return out;
  }

  /** Flat [id, count, dmg, ...]: 36 main, 4 armor, 1 offhand (41 stacks), then the
   *  enchantment tail (see serializeExtras) — old saves simply end after 123 numbers. */
  serialize(): number[] {
    const all = [...this.slots, ...this.armor, this.offhand];
    const out: number[] = [];
    for (const s of all) out.push(s.id, s.count, s.dmg);
    serializeExtras(all, out);
    return out;
  }

  deserialize(flat: readonly number[] | undefined): void {
    if (!flat) return;
    const all = [...this.slots, ...this.armor, this.offhand];
    for (let i = 0; i < all.length; i++) {
      const id = flat[i * 3] | 0, count = flat[i * 3 + 1] | 0, dmg = flat[i * 3 + 2] | 0;
      if (id > 0 && count > 0) setStack(all[i], { id, count, dmg });
      else clearStack(all[i]);
    }
    deserializeExtras(all, flat, all.length * 3);
    this.changed();
  }
}

